import { nanoid } from "nanoid";
import { computeColumnInsertIdxs, useActivePageId, useInsertPageProducts, usePageProducts } from "../../../../data";
import { colParams, compareIdx } from "../../../utils";
import { CurveIcon } from "../../../icons/CurveIcon";
import { SettingsIcon } from "../../../icons/SettingsIcon";
import { InsertCurveIcon } from "../../../icons/InsertCurveIcon";
import { tracePrecedentsSection } from "./trace-precedents";
import { showFormulaSection } from "./show-formula";
import { editCurve, copyCurveAsCopy } from "../../../actions";
import { CreateFromCopyIcon } from "../../../icons/CreateFromCopyIcon";
import { EditPencilIcon } from "../../../icons/EditPencilIcon";
import type { ActionContext } from "../../../actions/types";
import type { PageProductsFragmentFragment } from "../../../../__generated__/gql/graphql";
import type { MenuConfig } from "../../types";
import { any, none, allColumns, anyColumn, column, featureDisabled, selection, recipes } from "../../disabled-helpers";
import { GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP } from "../../../curve-management/globalisedFolders";
import { useUserId } from "../../../../context/auth/hooks";

function isNonManagerGlobalisedCurve(ctx: ActionContext): boolean {
  if (ctx.isUmiInternalAdmin) return false;
  const packageId = ctx.headerParams?.packageId;
  return packageId != null && ctx.globalisedPackageIds.has(packageId);
}

const createCurveAsCopyMenuItem: MenuConfig = {
  id: "create-curve-as-copy",
  label: "Create new Curve as copy",
  icon: <CreateFromCopyIcon />,
  disabled: any(
    selection.isMultiColumn,
    featureDisabled("createCurve"),
    none(allColumns(column.hasWriteAccess)),
    anyColumn(column.isCannedOrSourced),
  ),
  tooltip: (ctx) => {
    const col = ctx.api.getColumn(ctx.columnId);
    const headerParams = colParams(col);
    const hasWritePermission = headerParams?.permissions?.some((p) => p.permission === "write") ?? false;

    return ctx.disabledFeatures?.createCurve
      ? "Please upgrade your subscription to access this feature"
      : !hasWritePermission
        ? "You don't have write permission for this package"
        : undefined;
  },
  action: (ctx) => () => copyCurveAsCopy(ctx),
};

function buildCurveSubMenu(_ctx: ActionContext): MenuConfig[] {
  return [
    {
      id: "edit-curve",
      label: "Edit Curve",
      icon: <EditPencilIcon />,
      disabled: any(
        selection.isMultiColumn,
        featureDisabled("editCurve"),
        none(allColumns(column.hasWriteAccess)),
        anyColumn(column.isCannedOrSourced),
        isNonManagerGlobalisedCurve,
      ),
      tooltip: (ctx) => {
        const column = ctx.api.getColumn(ctx.columnId);
        const headerParams = colParams(column);
        const hasWritePermission = headerParams?.permissions?.some((p) => p.permission === "write") ?? false;

        if (ctx.disabledFeatures?.editCurve) {
          return "Please upgrade your subscription to access this feature";
        }
        if (!hasWritePermission) {
          return "You don't have write permission for this package";
        }
        if (isNonManagerGlobalisedCurve(ctx)) {
          return GLOBALISED_NON_MANAGER_EDIT_MENU_TOOLTIP;
        }
        return undefined;
      },
      action: (ctx) => () => editCurve(ctx),
    },
    createCurveAsCopyMenuItem,
    {
      id: "curve-settings",
      label: "Curve settings",
      icon: <SettingsIcon />,
      disabled: selection.isMultiColumn,
      action: (ctx) => () =>
        ctx.curveSettingsModal?.open({ curveName: ctx.headerName, columnId: ctx.columnId, productId: ctx.productId }),
    },
    { ...(showFormulaSection[0] as Exclude<MenuConfig, "separator">) },
    { ...(tracePrecedentsSection[0] as Exclude<MenuConfig, "separator">), isNew: false },
    {
      id: "insert-curve-submenu",
      label: "Insert Curve",
      icon: <InsertCurveIcon />,
      action: () => {},
      subMenu: [
        {
          id: "new-curve",
          label: "New Curve",
          disabled: featureDisabled("insertCurve"),
          tooltip: (ctx) =>
            ctx.disabledFeatures?.insertCurve ? "Please upgrade your subscription to access this feature" : undefined,
          action: (ctx) => () => ctx.insertCurveModal.open({ curveWidth: ctx.defaultCurveColumnWidth }),
        },
        {
          id: "duplicate-curve",
          label: "Duplicate",
          disabled: any(
            featureDisabled("insertCurve"),
            selection.isMultiColumn,
            none(allColumns(column.isCannedOrSourced)),
          ),
          tooltip: (ctx) =>
            ctx.disabledFeatures?.insertCurve ? "Please upgrade your subscription to access this feature" : undefined,
          action: (ctx) => () => ctx.insertCurveDuplicate(ctx.columnId, ctx.columnWidth ?? ctx.defaultCurveColumnWidth),
        },
      ],
    },
  ];
}

export const useInsertCurveDuplicate = () => {
  const insert = useInsertPageProducts();
  const userId = useUserId();
  const pageId = useActivePageId();
  const products = usePageProducts(pageId);

  return (id: string, columnWidth: number) => {
    const sorted = products.data?.sort(compareIdx);
    const current = sorted?.findIndex((x) => x.grid_id === id);

    if (current === undefined || current === -1 || !sorted) return console.error("Missing column");

    const product = sorted[current];

    const [idx] = computeColumnInsertIdxs(sorted, 1, "right_of_current", id);

    const newColId = nanoid();

    const newColumn = {
      id: newColId,
      grid_id: newColId,
      idx,
      product_id: product?.product_id,
      user_id: userId,
      page_id: pageId,
      column_type: "product",
      column_width: columnWidth,
      column_field_selector: product?.column_field_selector,
      decimal_places: product?.decimal_places,
      thousands_separator: product?.thousands_separator,
    } satisfies PageProductsFragmentFragment;

    return insert.mutateAsync([newColumn]);
  };
};

export const curveSection: MenuConfig[] = [
  {
    id: "curve",
    label: "Curve",
    icon: <CurveIcon />,
    disabled: recipes.productOnly,
    action: () => {},
    subMenu: buildCurveSubMenu,
  },
];
